import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import { useAuth } from './AuthContext';
import type { AgentChatMessage, AgentPageContext } from '../types';

interface AgentContextType {
  agentEnabled: boolean;
  /** True only after status is known and the assistant is not configured. */
  showLegacyHelp: boolean;
  pageContext: AgentPageContext;
  setPageContext: (partial: Partial<AgentPageContext>) => void;
  messages: AgentChatMessage[];
  previousResponseId: string | null;
  setMessages: React.Dispatch<React.SetStateAction<AgentChatMessage[]>>;
  setPreviousResponseId: (value: string | null) => void;
}

const AgentContext = createContext<AgentContextType | undefined>(undefined);

const EMPTY_CONTEXT: AgentPageContext = {
  pageId: 'unknown',
  route: '',
};

function getSessionStorageKey(userId?: string): string {
  return `aardvark-agent-chat:${userId ?? 'anonymous'}`;
}

type PersistedAgentState = {
  messages: AgentChatMessage[];
  previousResponseId: string | null;
};

function normaliseContext(context: AgentPageContext): string {
  return JSON.stringify({
    pageId: context.pageId,
    pageTitle: context.pageTitle,
    route: context.route,
    viewMode: context.viewMode,
    filters: context.filters ?? {},
    visibleSections: context.visibleSections ?? [],
    suggestedPrompts: context.suggestedPrompts ?? [],
  });
}

export const AgentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [agentEnabled, setAgentEnabled] = useState(false);
  const [statusResolved, setStatusResolved] = useState(false);
  const [pageContext, setPageContextState] = useState<AgentPageContext>(EMPTY_CONTEXT);
  const [messages, setMessages] = useState<AgentChatMessage[]>([]);
  const [previousResponseId, setPreviousResponseId] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.id) {
      setAgentEnabled(false);
      setStatusResolved(false);
      return;
    }

    let cancelled = false;
    setStatusResolved(false);

    const loadStatus = async () => {
      try {
        const res = await apiService.get<{ configured: boolean }>(API_ENDPOINTS.AGENT.STATUS);
        if (!cancelled) {
          setAgentEnabled(!!res.data?.configured);
        }
      } catch {
        if (!cancelled) {
          setAgentEnabled(false);
        }
      } finally {
        if (!cancelled) {
          setStatusResolved(true);
        }
      }
    };
    void loadStatus();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    const key = getSessionStorageKey(user?.id);
    let cancelled = false;
    let nextMessages: AgentChatMessage[] = [];
    let nextPreviousResponseId: string | null = null;
    try {
      const raw = sessionStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw) as PersistedAgentState;
        nextMessages = Array.isArray(parsed.messages) ? parsed.messages : [];
        nextPreviousResponseId = parsed.previousResponseId ?? null;
      }
    } catch {
      nextMessages = [];
      nextPreviousResponseId = null;
    }

    queueMicrotask(() => {
      if (cancelled) return;
      setMessages(nextMessages);
      setPreviousResponseId(nextPreviousResponseId);
    });

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    const key = getSessionStorageKey(user?.id);
    const payload: PersistedAgentState = { messages, previousResponseId };
    sessionStorage.setItem(key, JSON.stringify(payload));
  }, [messages, previousResponseId, user?.id]);

  const setPageContext = useCallback((partial: Partial<AgentPageContext>) => {
    setPageContextState((prev) => {
      const next = { ...prev, ...partial };
      return normaliseContext(next) === normaliseContext(prev) ? prev : next;
    });
  }, []);

  const showLegacyHelp = statusResolved && !agentEnabled;

  const value = useMemo<AgentContextType>(() => ({
    agentEnabled,
    showLegacyHelp,
    pageContext,
    setPageContext,
    messages,
    previousResponseId,
    setMessages,
    setPreviousResponseId,
  }), [agentEnabled, showLegacyHelp, pageContext, setPageContext, messages, previousResponseId]);

  return <AgentContext.Provider value={value}>{children}</AgentContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export function useAgentContext(): AgentContextType {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error('useAgentContext must be used within AgentProvider');
  }
  return context;
}
